import React, { useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BookOpen, GripVertical, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { CourseFormModule } from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";

interface CourseStructureNavProps {
  modules: CourseFormModule[];
  /** "details" or a module id. */
  activeKey: string;
  estimatedHours: string;
  onSelect: (key: string) => void;
  onAddModule: () => void;
  onReorder: (modules: CourseFormModule[]) => void;
}

const navItemClass = (active: boolean) =>
  cn(
    "flex-1 flex items-center gap-3 px-3 py-2.5 text-left rounded-md transition-colors min-w-0",
    active
      ? "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50",
  );

interface SortableModuleItemProps {
  module: CourseFormModule;
  index: number;
  active: boolean;
  onSelect: () => void;
}

function SortableModuleItem({
  module,
  index,
  active,
  onSelect,
}: SortableModuleItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div className="flex items-center gap-1" ref={setNodeRef} style={style}>
      <button
        aria-label={`Reorder module ${index + 1}`}
        className="cursor-grab text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-1"
        type="button"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <button className={navItemClass(active)} onClick={onSelect} type="button">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700 text-xs text-gray-600 dark:text-gray-300">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm truncate">
            {module.title || `Module ${index + 1}`}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {module.blocks.length} content block
            {module.blocks.length === 1 ? "" : "s"}
          </p>
        </div>
      </button>
    </div>
  );
}

/**
 * The left "Course Structure" column on the Edit screen. Shows a summary, a
 * Course Details nav item, the numbered drag-to-reorder module list, and an
 * Add Module action.
 */
export function CourseStructureNav({
  modules,
  activeKey,
  estimatedHours,
  onSelect,
  onAddModule,
  onReorder,
}: CourseStructureNavProps) {
  const moduleCount = modules.length;
  const hoursLabel = estimatedHours ? `${estimatedHours}h` : "0h";

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = modules.findIndex((m) => m.id === active.id);
      const newIndex = modules.findIndex((m) => m.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      onReorder(arrayMove(modules, oldIndex, newIndex));
    },
    [modules, onReorder],
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
        <h2 className="text-base font-normal text-gray-900 dark:text-white">
          Course Structure
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {moduleCount} module{moduleCount === 1 ? "" : "s"} · {hoursLabel}
        </p>
      </div>

      <div className="p-2">
        <button
          className={cn("w-full", navItemClass(activeKey === "details"))}
          onClick={() => onSelect("details")}
          type="button"
        >
          <BookOpen className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">Course Details</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              General information
            </p>
          </div>
        </button>
      </div>

      <div className="px-4 pt-2 pb-1">
        <p className="text-xs font-normal text-gray-500 dark:text-gray-400">
          Modules
        </p>
      </div>

      <div className="p-2 pt-0 space-y-1">
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={modules.map((m) => m.id)}
            strategy={verticalListSortingStrategy}
          >
            {modules.map((module, index) => (
              <SortableModuleItem
                active={activeKey === module.id}
                index={index}
                key={module.id}
                module={module}
                onSelect={() => onSelect(module.id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button
          className="w-full flex items-center gap-2 px-3 py-2.5 text-left rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
          onClick={onAddModule}
          type="button"
        >
          <Plus className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          Add Module
        </button>
      </div>
    </div>
  );
}
